import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import moment from 'moment';
import { crearRequisicion } from '../../services/requisiciones/requisicionService';
import { marcarActividadesComoRequeridas } from '../../services/actividades/actividadService';
import { getBadgeVencimiento } from '../../helpers/actividadVencimiento';
import Swal from 'sweetalert2';
import { useAuth } from '../../hooks/useAuth';

const ModalConfirmarRequisicion = ({ show, onHide, actividadesIds, actividades, onCrear }) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    observaciones: ''
  });


  const handleChange = (campo, valor) => {
    setFormData(prev => ({ ...prev, [campo]: valor }));
  };

  const validarFormulario = () => {
    if (!formData.titulo || formData.titulo.trim() === '') {
      Swal.fire({ icon: 'warning', text: 'El título de la requisición es requerido' });
      return false;
    }
    return true;
  };

  const handleCrearRequisicion = async () => {
    if (!validarFormulario()) return;

    try {
      setLoading(true);

      // Construir items de la requisición (misma estructura que RequisicionFormPage)
      const items = actividades.map(actividad => {
        
        // Validar que exista equipo
        if (!actividad.equipo || !actividad.equipo._id) {
          console.error('Actividad sin equipo:', actividad);
          throw new Error(`La actividad ${actividad._id} no tiene equipo asignado`);
        }

        return {
          actividad: actividad._id,
          patron: actividad.equipo._id,
          tipoActividad: actividad.tipoActividad,
          observacion: actividad.observaciones || ''
        };
      });

      // Crear la requisición
      const dataRequisicion = {
        titulo: formData.titulo,
        observacionComercial: formData.observaciones,
        areaOrigen: userData?.area || '',
        items: items
      };

      console.log('Datos a enviar al backend:', dataRequisicion);

      const responseRequisicion = await crearRequisicion(dataRequisicion);

      if (responseRequisicion?.data?.data) {
        // Marcar actividades como "Requerida" y asociarlas a la requisición
        //await marcarActividadesComoRequeridas(actividadesIds, responseRequisicion.data._id);

        Swal.fire({ 
          icon: 'success', 
          text: 'Requisición creada correctamente' 
        });

        onCrear();
        
        // Navegar a la página de detalle de la requisición
        navigate(`/requisiciones/${responseRequisicion.data.data._id}`);
      }
    } catch (error) {
      console.error('Error al crear requisición:', error);
      Swal.fire({ 
        icon: 'error', 
        text: error.response?.data?.message || 'Error al crear la requisición' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clipboard-check me-2"></i>
          Crear Requisición desde Actividades
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <i className="bi bi-info-circle me-2"></i>
          Se creará una requisición con las <strong>{actividades.length}</strong> actividad{actividades.length !== 1 ? 'es' : ''} seleccionada{actividades.length !== 1 ? 's' : ''}. 
          Las actividades pasarán al estado <Badge bg="warning">Requerida</Badge> y quedarán asociadas a esta requisición.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Título de la Requisición <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Ej: Requisición de patrones para calibraciones de marzo"
            value={formData.titulo}
            onChange={(e) => handleChange('titulo', e.target.value)}
            maxLength={200}
          />
          <Form.Text className="text-muted">
            {formData.titulo.length}/200 caracteres
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Observaciones Comercial</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Observaciones adicionales sobre la requisición..."
            value={formData.observaciones}
            onChange={(e) => handleChange('observaciones', e.target.value)}
          />
        </Form.Group>

        <h6 className="mb-3">
          <i className="bi bi-list-check me-2"></i>
          Actividades Incluidas ({actividades.length})
        </h6>

        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table striped bordered hover size="sm">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <tr>
                <th>#</th>
                <th>Equipo</th>
                <th>Tipo</th>
                <th>Fecha Vencimiento</th>
                <th>Estado</th>
                <th>Patrón</th>
                <th>Magnitud</th>
              </tr>
            </thead>
            <tbody>
              {actividades.map((actividad, index) => {
                const badgeInfo = getBadgeVencimiento(actividad.FechaVencimiento);
                
                return (
                  <tr key={actividad._id}>
                    <td>{index + 1}</td>
                    <td>{actividad.equipo?.item || 'N/A'}</td>
                    <td>{actividad.tipoActividad}</td>
                    <td>
                      {moment(actividad.FechaVencimiento).format('DD/MM/YYYY')}
                    </td>
                    <td>
                      <Badge bg={badgeInfo.variant}>
                        {badgeInfo.label}
                      </Badge>
                    </td>
                    <td>{actividad.equipo?.item || 'N/A'}</td>
                    <td>{actividad.magnitud?.nombre || 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleCrearRequisicion} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creando requisición...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              Crear Requisición
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalConfirmarRequisicion;
